import React from "react";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import StatusBadge from "./StatusBadge";

const OrganizationTable = ({
  organizations,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onApprove,
  onReject,
  onSuspend,
  onReactivate,
  onViewDetails,
}) => {
  const allSelected = organizations?.length > 0 && organizations?.every((o) => selectedIds?.includes(o?.id));
  const someSelected = organizations?.some((o) => selectedIds?.includes(o?.id));

  return (
    <>
      {/* Desktop Table */}
      <div className="hidden lg:block overflow-x-auto rounded-xl border shadow-sm" style={{ borderColor: "var(--color-border)" }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ backgroundColor: "var(--color-muted)" }}>
              <th className="px-4 py-3 text-left w-10">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                  onChange={onToggleSelectAll}
                  className="w-4 h-4 rounded cursor-pointer accent-teal-600"
                />
              </th>
              {["NIT", "Razón Social", "Correo", "Teléfono", "Ciudad", "Fecha Registro", "Estado", "Acciones"]?.map((h) => (
                <th key={h} className="px-4 py-3 text-left font-caption font-semibold text-xs uppercase tracking-wide" style={{ color: "var(--color-muted-foreground)" }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {organizations?.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-12 text-center font-caption" style={{ color: "var(--color-muted-foreground)" }}>
                  <div className="flex flex-col items-center gap-2">
                    <Icon name="SearchX" size={32} color="var(--color-muted-foreground)" />
                    <span>No se encontraron registros</span>
                  </div>
                </td>
              </tr>
            ) : (
              organizations?.map((org, idx) => (
                <tr
                  key={org?.id}
                  className="border-t transition-colors hover:bg-gray-50"
                  style={{ borderColor: "var(--color-border)", backgroundColor: idx % 2 === 0 ? "var(--color-card)" : "var(--color-background)" }}
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds?.includes(org?.id)}
                      onChange={() => onToggleSelect(org?.id)}
                      className="w-4 h-4 rounded cursor-pointer accent-teal-600"
                    />
                  </td>
                  <td className="px-4 py-3 font-data text-xs whitespace-nowrap" style={{ color: "var(--color-foreground)" }}>
                    {org?.nit}
                  </td>
                  <td className="px-4 py-3 font-caption font-medium max-w-[180px]">
                    <span className="line-clamp-1" style={{ color: "var(--color-foreground)" }}>{org?.businessName}</span>
                  </td>
                  <td className="px-4 py-3 font-caption text-xs max-w-[160px]">
                    <span className="line-clamp-1" style={{ color: "var(--color-muted-foreground)" }}>{org?.email}</span>
                  </td>
                  <td className="px-4 py-3 font-caption text-xs whitespace-nowrap" style={{ color: "var(--color-muted-foreground)" }}>
                    {org?.phone || "—"}
                  </td>
                  <td className="px-4 py-3 font-caption text-xs whitespace-nowrap" style={{ color: "var(--color-muted-foreground)" }}>
                    {org?.city || "—"}
                  </td>
                  <td className="px-4 py-3 font-caption text-xs whitespace-nowrap" style={{ color: "var(--color-muted-foreground)" }}>
                    {org?.registrationDate}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={org?.status} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onViewDetails(org)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                        title="Ver detalles"
                      >
                        <Icon name="Eye" size={15} color="var(--color-secondary)" />
                      </button>
                      {org?.status === "PENDING" && (
                        <>
                          <button
                            onClick={() => onApprove(org)}
                            className="p-1.5 rounded-lg hover:bg-emerald-50 transition-colors"
                            title="Aprobar"
                          >
                            <Icon name="UserCheck" size={15} color="var(--color-success)" />
                          </button>
                          <button
                            onClick={() => onReject(org)}
                            className="p-1.5 rounded-lg hover:bg-red-50 transition-colors"
                            title="Rechazar"
                          >
                            <Icon name="UserX" size={15} color="var(--color-error)" />
                          </button>
                        </>
                      )}
                      {org?.status === "ACTIVE" && (
                        <button
                          onClick={() => onSuspend(org)}
                          className="p-1.5 rounded-lg hover:bg-amber-50 transition-colors"
                          title="Suspender"
                        >
                          <Icon name="PauseCircle" size={15} color="var(--color-warning)" />
                        </button>
                      )}
                      {org?.status === "SUSPENDED" && (
                        <button
                          onClick={() => onReactivate(org)}
                          className="p-1.5 rounded-lg hover:bg-teal-50 transition-colors"
                          title="Reactivar"
                        >
                          <Icon name="PlayCircle" size={15} color="var(--color-primary)" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Mobile/Tablet Card View */}
      <div className="lg:hidden space-y-3">
        {organizations?.length === 0 ? (
          <div className="rounded-xl border p-8 text-center" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-card)" }}>
            <Icon name="SearchX" size={32} color="var(--color-muted-foreground)" />
            <p className="mt-2 font-caption text-sm" style={{ color: "var(--color-muted-foreground)" }}>No se encontraron registros</p>
          </div>
        ) : (
          organizations?.map((org) => (
            <div key={org?.id} className="rounded-xl border p-4 shadow-sm" style={{ borderColor: "var(--color-border)", backgroundColor: "var(--color-card)" }}>
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2 min-w-0">
                  <input
                    type="checkbox"
                    checked={selectedIds?.includes(org?.id)}
                    onChange={() => onToggleSelect(org?.id)}
                    className="w-4 h-4 rounded cursor-pointer accent-teal-600 flex-shrink-0"
                  />
                  <div className="min-w-0">
                    <p className="font-caption font-semibold text-sm line-clamp-1" style={{ color: "var(--color-foreground)" }}>{org?.businessName}</p>
                    <p className="font-data text-xs mt-0.5" style={{ color: "var(--color-muted-foreground)" }}>NIT: {org?.nit}</p>
                  </div>
                </div>
                <StatusBadge status={org?.status} />
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs font-caption mb-3" style={{ color: "var(--color-muted-foreground)" }}>
                <span className="truncate">{org?.email}</span>
                <span>{org?.city || "—"}</span>
                <span>{org?.phone || "—"}</span>
                <span>{org?.registrationDate}</span>
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t" style={{ borderColor: "var(--color-border)" }}>
                <Button variant="outline" size="xs" iconName="Eye" iconPosition="left" onClick={() => onViewDetails(org)}>Ver</Button>
                {org?.status === "PENDING" && (
                  <>
                    <Button variant="success" size="xs" iconName="UserCheck" iconPosition="left" onClick={() => onApprove(org)}>Aprobar</Button>
                    <Button variant="destructive" size="xs" iconName="UserX" iconPosition="left" onClick={() => onReject(org)}>Rechazar</Button>
                  </>
                )}
                {org?.status === "ACTIVE" && (
                  <Button variant="warning" size="xs" iconName="PauseCircle" iconPosition="left" onClick={() => onSuspend(org)}>Suspender</Button>
                )}
                {org?.status === "SUSPENDED" && (
                  <Button variant="default" size="xs" iconName="PlayCircle" iconPosition="left" onClick={() => onReactivate(org)}>Reactivar</Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

export default OrganizationTable;